import { useQuery } from '@tanstack/react-query'
import client from '../api/client.js'

async function fetchStoreDetail(storeId) {
  const { data } = await client.get(`/stores/${storeId}`)
  return data
}

async function fetchStoreList({ page = 1, pageSize = 50, search = '', dc = '', signal = '', sortBy = 'store_id' }) {
  const params = { page, page_size: pageSize, sort_by: sortBy }
  if (search) params.search = search
  if (dc) params.dc = dc
  if (signal) params.signal = signal
  const { data } = await client.get('/stores', { params })
  return data
}

async function fetchStoreActions(storeId) {
  const { data } = await client.get(`/stores/${storeId}/actions`)
  return data
}

async function fetchActionExplain(actionId) {
  const { data } = await client.get(`/actions/${actionId}/explain`)
  return data
}

async function fetchTrafficPrediction(storeId, days = 7) {
  const { data } = await client.get(`/stores/${storeId}/traffic-prediction`, { params: { days } })
  return data
}

export function useStoreTrafficPrediction(storeId, { days = 7 } = {}) {
  return useQuery({
    queryKey: ['store-traffic-prediction', storeId, days],
    queryFn: () => fetchTrafficPrediction(storeId, days),
    enabled: Boolean(storeId),
    staleTime: 10 * 60 * 1000,   // matches the model + weather cache cadence
    retry: 1,
  })
}

export function useStore(storeId) {
  return useQuery({
    queryKey: ['store', storeId],
    queryFn: () => fetchStoreDetail(storeId),
    enabled: Boolean(storeId),
    staleTime: 3 * 60 * 1000,
    retry: 2,
  })
}

export function useStoreList({ page = 1, pageSize = 50, search = '', dc = '', signal = '', sortBy = 'store_id' } = {}) {
  return useQuery({
    queryKey: ['stores', { page, pageSize, search, dc, signal, sortBy }],
    queryFn: () => fetchStoreList({ page, pageSize, search, dc, signal, sortBy }),
    staleTime: 2 * 60 * 1000,
    keepPreviousData: true,
    retry: 2,
  })
}

export function useStoreActions(storeId) {
  return useQuery({
    queryKey: ['store-actions', storeId],
    queryFn: () => fetchStoreActions(storeId),
    enabled: Boolean(storeId),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })
}

export function useActionExplain(actionId) {
  return useQuery({
    queryKey: ['action-explain', actionId],
    queryFn: () => fetchActionExplain(actionId),
    enabled: Boolean(actionId),
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days — matches server cache TTL
    retry: 1,
  })
}
